
import React, { useState } from 'react';
import { AppMode } from '../types';
import BrandTiles from './landing/BrandTiles';

interface LandingPageProps {
  ui: any;
  currentLang: string;
  onUpdateLang: (lang: string) => void;
  onEnter: () => void;
  onViewAbout: () => void;
  onDirectLink: (mode: AppMode) => void;
}

const LANGUAGES = ['English', 'Spanish', 'French', 'German', 'Italian', 'Portuguese', 'Japanese', 'Mandarin'];

const LandingPage: React.FC<LandingPageProps> = ({ ui, currentLang, onUpdateLang, onEnter, onViewAbout, onDirectLink }) => {
  const [showProtocol, setShowProtocol] = useState(false);
  const [agreed, setAgreed] = useState(false);

  const handleLanguageChange = (lang: string) => {
    onUpdateLang(lang);
    localStorage.setItem('groovefx_lang', lang);
  };

  const handleEnterClick = () => {
    if (localStorage.getItem('groovefx_agreed') === 'true') {
      onEnter();
    } else {
      setShowProtocol(true);
    }
  };

  const handleAccept = () => {
    localStorage.setItem('groovefx_agreed', 'true');
    onEnter();
  };

  const content = ui.landing;

  return (
    <div className="min-h-screen bg-[#05040f] text-white font-sans flex flex-col relative overflow-hidden text-center selection:bg-groove-pink selection:text-white">
      <style>{`
        .animate-title-gradient {
          background: linear-gradient(to right, #ff00d2, #00d2ff, #ffffff, #00d2ff, #ff00d2);
          background-size: 200% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: title-shine 10s linear infinite;
        }
        @keyframes title-shine {
          to { background-position: 200% center; }
        }
        .imagineering-signature {
          font-family: 'Inter', sans-serif; font-weight: 300;
          letter-spacing: 0.3em; text-transform: lowercase;
          background: linear-gradient(to right, #00d2ff, #ff00d2);
          -webkit-background-clip: text; -webkit-text-fill-color: transparent;
          display: inline-block;
          padding-bottom: 0.5rem; /* Increased padding to prevent clipping of 'g' descenders */
        }
      `}</style>
      
      <header className="relative z-50 p-6 md:p-8 flex justify-between items-center w-full max-w-7xl mx-auto">
        <div className="flex items-center gap-3 text-left">
          <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-groove-accent flex items-center justify-center shadow-lg shadow-groove-accent/30">
            <i className="fa-solid fa-wave-square text-black text-lg md:text-xl"></i>
          </div>
          <div className="flex flex-col">
            <span className="font-bold tracking-tight text-xl md:text-2xl text-white leading-none">GrooveFX</span>
            <span className="text-[7px] md:text-[9px] font-black uppercase tracking-[0.2em] md:tracking-[0.3em] text-groove-accent mt-1 leading-none">{content.tagline}</span>
          </div>
        </div>
        
        <div className="flex items-center gap-4 md:gap-10">
           <div className="bg-white/5 border border-white/20 rounded-full px-4 py-2 flex items-center gap-3 backdrop-blur-md">
            <i className="fa-solid fa-globe text-[10px] text-white"></i>
            <select value={currentLang} onChange={(e) => handleLanguageChange(e.target.value)} className="bg-transparent text-[9px] font-black uppercase tracking-widest text-white outline-none cursor-pointer">
              {LANGUAGES.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
        </div>
      </header>

      <main className="relative z-10 flex-1 flex flex-col items-center pt-12 md:pt-24 pb-20 overflow-y-auto custom-scrollbar px-6">
        <h1 className="text-6xl md:text-[10rem] font-black tracking-tighter leading-none animate-title-gradient mb-2 md:mb-4">
          GrooveFX
        </h1>
        
        <div className="flex items-center justify-center gap-6 md:gap-10 mb-16 md:mb-20 py-2">
          <i className="fa-solid fa-star text-lg md:text-2xl text-[#00d2ff] animate-pulse"></i>
          <span className="imagineering-signature text-2xl md:text-5xl">imagineering</span>
          <i className="fa-solid fa-star text-lg md:text-2xl text-[#ff00d2] animate-pulse"></i>
        </div>

        <p className="text-lg md:text-2xl text-slate-300 font-light leading-relaxed max-w-4xl mx-auto mb-16 md:mb-24 px-4">
          {content.blurb}
        </p>

        <BrandTiles />

        <div className="w-full max-w-5xl space-y-6 md:space-y-10 mb-24 text-left">
           {/* BYOK INFRASTRUCTURE */}
           <div className="p-10 md:p-14 rounded-[3rem] md:rounded-[4rem] bg-white/[0.03] border border-white/10 backdrop-blur-3xl space-y-6 md:space-y-8 shadow-2xl">
              <div className="w-12 h-12 rounded-xl bg-groove-accent/10 border border-groove-accent/20 flex items-center justify-center">
                 <i className="fa-solid fa-key text-groove-accent text-lg"></i>
              </div>
              <h3 className="text-2xl md:text-3xl font-black text-white uppercase tracking-widest leading-none">BYOK INFRASTRUCTURE</h3>
              <p className="text-base md:text-lg text-slate-400 font-light leading-relaxed">Professional multimodal computing requires raw access. GrooveFX connects you directly to the source. Basic text chat is free, but high-fidelity audio and video tools require your own developer keys. Pay only wholesale rates with no middleman.</p>
              <button onClick={() => onDirectLink(AppMode.BILLING)} className="text-[10px] font-black uppercase text-groove-accent tracking-[0.3em] hover:text-white transition-colors">
                SETUP PAID GEMINI ACCOUNT {" > "}
              </button>
           </div>

           {/* MULTIMODAL HUB */}
           <div className="p-10 md:p-14 rounded-[3rem] md:rounded-[4rem] bg-white/[0.03] border border-white/10 backdrop-blur-3xl space-y-6 md:space-y-8 shadow-2xl">
              <div className="w-12 h-12 rounded-xl bg-groove-accent/10 border border-groove-accent/20 flex items-center justify-center">
                 <i className="fa-solid fa-microphone text-groove-accent text-lg"></i>
              </div>
              <h3 className="text-2xl md:text-3xl font-black text-white uppercase tracking-widest leading-none">MULTIMODAL HUB</h3>
              <p className="text-base md:text-lg text-slate-400 font-light leading-relaxed">Real-time voice and text handshake with advanced Gemini 2.5 Flash native audio. Experience ultra-low latency conversational AI designed for fluid, human-like interaction. Sovereignty prioritized.</p>
           </div>

           {/* STUDIO PASS */}
           <div className="p-10 md:p-14 rounded-[3rem] md:rounded-[4rem] bg-white/[0.03] border border-white/10 backdrop-blur-3xl space-y-6 md:space-y-8 shadow-2xl">
              <div className="w-12 h-12 rounded-xl bg-groove-accent/10 border border-groove-accent/20 flex items-center justify-center">
                 <i className="fa-solid fa-id-card-clip text-groove-accent text-lg"></i>
              </div>
              <h3 className="text-2xl md:text-3xl font-black text-white uppercase tracking-widest leading-none">STUDIO PASS</h3>
              <p className="text-base md:text-lg text-slate-400 font-light leading-relaxed">Professional tier access to Veo Video generation and high-fidelity Visual Studio. Archive your creations in the Visual Studio and manifest unique AI personas with persistent memory.</p>
           </div>
           
           {/* SAFETY & PARENTAL CONTROLS */}
           <div className="p-10 md:p-14 rounded-[3rem] md:rounded-[4rem] bg-white/[0.03] border border-white/10 backdrop-blur-3xl space-y-6 md:space-y-8 shadow-2xl">
              <div className="w-12 h-12 rounded-xl bg-groove-accent/10 border border-groove-accent/20 flex items-center justify-center">
                 <i className="fa-solid fa-shield-halved text-groove-accent text-lg"></i>
              </div>
              <h3 className="text-2xl md:text-3xl font-black text-white uppercase tracking-widest leading-none">SAFETY & PARENTAL CONTROLS</h3>
              <p className="text-base md:text-lg text-slate-400 font-light leading-relaxed">We are committed to providing a safe environment. This service is strictly for users 16 and older. We strongly encourage parents to use supervised tools like Google Family Link to manage digital wellness.</p>
              <a href="https://families.google/familylink/" target="_blank" rel="noopener noreferrer" className="inline-block text-[10px] font-black uppercase text-groove-accent tracking-[0.3em] hover:text-white transition-colors">
                LEARN ABOUT GOOGLE FAMILY LINK {" > "}
              </a>
           </div>

           {/* PWA MOBILE SETUP */}
           <div className="p-10 md:p-14 rounded-[3rem] md:rounded-[4rem] bg-white/[0.03] border border-white/10 backdrop-blur-3xl space-y-6 md:space-y-8 shadow-2xl">
              <div className="w-12 h-12 rounded-xl bg-groove-accent/10 border border-groove-accent/20 flex items-center justify-center">
                 <i className="fa-solid fa-mobile-screen-button text-groove-accent text-lg"></i>
              </div>
              <h3 className="text-2xl md:text-3xl font-black text-white uppercase tracking-widest leading-none">PWA MOBILE SETUP</h3>
              <p className="text-base md:text-lg text-slate-400 font-light leading-relaxed">Experience GrooveFX natively on your device. Chrome (Android): Tap 'Three Dots' {" > "} 'Install App'. Safari (iOS): Tap 'Share' {" > "} 'Add to Home Screen'. No store download required.</p>
           </div>
        </div>

        <h2 className="text-xl md:text-2xl font-black text-white mb-8 md:mb-10 tracking-[0.4em] uppercase opacity-60">{content.ready}</h2>
        <button onClick={handleEnterClick} className="w-full md:w-auto px-16 md:px-24 py-8 md:py-10 bg-white text-black font-black text-xl md:text-2xl rounded-[2.5rem] md:rounded-[3rem] shadow-2xl hover:bg-groove-pink hover:text-white transition-all uppercase tracking-[0.4em] md:tracking-[0.5em]">
          {content.enter}
        </button>
      </main>

      <footer className="relative z-10 p-12 md:p-20 border-t border-white/5 bg-black/40 backdrop-blur-3xl">
        <p className="text-[10px] md:text-[12px] text-slate-500 font-black tracking-[0.4em] md:tracking-[0.5em] uppercase mb-10 md:mb-12">{content.footer.powered}</p>
        <div className="flex flex-wrap justify-center items-center gap-8 md:gap-20 text-[10px] md:text-[12px] font-black text-slate-400 uppercase tracking-widest">
           <a href="https://families.google/familylink/" target="_blank" rel="noopener noreferrer" className="hover:text-groove-accent transition-colors">GOOGLE FAMILY LINK</a>
           <button onClick={onViewAbout} className="hover:text-groove-pink transition-colors">Terms of Service & Privacy</button>
           <span>{content.footer.copyright}</span>
        </div>
      </footer>

      {showProtocol && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-6 bg-black/95 backdrop-blur-3xl">
          <div className="max-w-3xl w-full bg-[#05040f] border border-white/10 rounded-[4rem] p-12 md:p-16 space-y-12 shadow-3xl">
            <h2 className="text-3xl md:text-4xl font-black uppercase tracking-widest text-groove-accent">Protocol Entry</h2>
            <div className="space-y-6 text-slate-300 text-left h-64 overflow-y-auto custom-scrollbar pr-4 text-lg md:text-xl font-light">
               <p>This service is for users 16 and older. GrooveFX Chat is NOT intended to replace professional care. If in crisis, please seek immediate help.</p>
            </div>
            <label className="flex items-center gap-6 cursor-pointer p-8 rounded-[2.5rem] bg-white/5 border border-white/10">
              <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="w-8 h-8 rounded-lg bg-black text-groove-pink border-white/20" />
              <span className="text-[10px] md:text-[12px] text-slate-400 font-black uppercase tracking-widest">I am 16+ and accept the terms</span>
            </label>
            <div className="flex gap-8">
               <button onClick={() => setShowProtocol(false)} className="flex-1 py-6 text-[10px] text-slate-500 font-black uppercase tracking-widest">Abort</button>
               <button disabled={!agreed} onClick={handleAccept} className="flex-1 py-6 bg-white text-black rounded-[2rem] font-black uppercase text-[10px] tracking-widest disabled:opacity-10">ACCEPT & ENTER</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
